import { present } from '@art-suite/art-core-ts';
import { ScanTemplateGroup, ScanTemplateLineItem, ScanTemplateOption, ScanTemplatePart, ScanTemplateTask } from '@kaiizen/kai-scan-template-system';

type FilterCacheEntry = {
  searchString: string;
  part: ScanTemplatePart;
};

export type ScanTemplateSearchCache = Map<string, FilterCacheEntry>;
export const createScanTemplateSearchCache = (): ScanTemplateSearchCache => new Map<string, FilterCacheEntry>();

const getTemplatePartSearchStringArray = (
  templatePart: ScanTemplatePart,
): (string | undefined)[] => [templatePart.name, templatePart.description, templatePart.category, templatePart.subtype];

export const getScanTemplatePartFilterFunction = (filterRegex?: RegExp) => (scanTemplatePart: ScanTemplatePart) => {
  if (!filterRegex) return true;
  const searchStringArray = getTemplatePartSearchStringArray(scanTemplatePart);
  return searchStringArray.some(searchString => searchString ? filterRegex.test(searchString) : false);
};

const getTemplateLineItemSearchStringArray = (lineItem: ScanTemplateLineItem): (string | undefined)[] => getTemplatePartSearchStringArray(lineItem);
const getTemplateOptionSearchStringArray = (option: ScanTemplateOption): (string | undefined)[] => [
  ...getTemplatePartSearchStringArray(option),
  ...(option.lineItemTemplates?.flatMap(lineItem => getTemplateLineItemSearchStringArray(lineItem)) ?? []),
];
const getTemplateTaskSearchStringArray = (task: ScanTemplateTask): (string | undefined)[] => [
  ...getTemplatePartSearchStringArray(task),
  ...(task.optionTemplates?.flatMap(option => getTemplateOptionSearchStringArray(option)) ?? []),
];

const getTemplateGroupSearchStringArray = (
  taskTemplatesById: Record<string, ScanTemplateTask>,
  group: ScanTemplateGroup,
  searchCache: ScanTemplateSearchCache,
): (string | undefined)[] => [
  ...getTemplatePartSearchStringArray(group),
  ...(group.taskTemplates?.flatMap(task => getCachedTemplateTaskSearchString(task, searchCache)) ?? []),
  ...(group.taskTemplateIds?.flatMap(taskId => getCachedTemplateTaskSearchString(taskTemplatesById[taskId], searchCache)) ?? []),
];

export const getCachedTemplateGroupSearchString = (
  taskTemplatesById: Record<string, ScanTemplateTask>,
  groupId: string,
  group: ScanTemplateGroup,
  searchCache: ScanTemplateSearchCache,
): string => {
  const entry = searchCache.get(groupId);
  if (!entry || entry.part !== group) { // if the entry is not found or the group has changed, update the cache
    const searchStringArray = getTemplateGroupSearchStringArray(taskTemplatesById, group, searchCache);
    const newEntry: FilterCacheEntry = { searchString: searchStringArray.filter(present).join('\n'), part: group };
    searchCache.set(groupId, newEntry);
    return newEntry.searchString;
  }
  return entry.searchString; // if the entry is found and the group has not changed, return the entry
};

export const getCachedTemplateTaskSearchString = (task: ScanTemplateTask, searchCache: ScanTemplateSearchCache): string => {
  const taskId = task.id;
  const entry = searchCache.get(taskId);
  if (!entry || entry.part !== task) { // if the entry is not found or the task has changed, update the cache
    const searchStringArray = getTemplateTaskSearchStringArray(task);
    const newEntry: FilterCacheEntry = { searchString: searchStringArray.join('\n'), part: task };
    searchCache.set(taskId, newEntry);
    return newEntry.searchString;
  }
  return entry.searchString; // if the entry is found and the task has not changed, return the entry
};

export const getFilterTemplateTaskFunction = (searchCache: ScanTemplateSearchCache, searchRegex?: RegExp) => {
  if (!searchRegex) return (task: ScanTemplateTask) => true;
  return (task: ScanTemplateTask) => searchRegex.test(getCachedTemplateTaskSearchString(task, searchCache));
};

export const getFilterTemplateGroupFunction = (
  taskTemplatesById: Record<string, ScanTemplateTask>,
  searchCache: ScanTemplateSearchCache,
  searchRegex?: RegExp,
) => {
  if (!searchRegex) return (group: ScanTemplateGroup) => true;
  return (group: ScanTemplateGroup) => searchRegex.test(getCachedTemplateGroupSearchString(taskTemplatesById, group.id, group, searchCache));
};

export const getFilterTemplateLineItemFunction = (searchCache: ScanTemplateSearchCache, searchRegex?: RegExp) => {
  if (!searchRegex) return (lineItem: ScanTemplateLineItem) => true;
  return (lineItem: ScanTemplateLineItem) => searchRegex.test(getTemplateLineItemSearchStringArray(lineItem).filter(present).join('\n'));
};

export const getFilterTemplateOptionFunction = (searchCache: ScanTemplateSearchCache, searchRegex?: RegExp) => {
  if (!searchRegex) return (option: ScanTemplateOption) => true;
  return (option: ScanTemplateOption) => searchRegex.test(getTemplateOptionSearchStringArray(option).filter(present).join('\n'));
};

export const getScanTemplateFilterFunctions = (
  taskTemplatesById: Record<string, ScanTemplateTask>,
  searchCache: ScanTemplateSearchCache,
  searchRegex?: RegExp,
) => {
  return {
    filterScanTemplateTask: getFilterTemplateTaskFunction(searchCache, searchRegex),
    filterScanTemplateGroup: getFilterTemplateGroupFunction(taskTemplatesById, searchCache, searchRegex),
    filterScanTemplateLineItem: getFilterTemplateLineItemFunction(searchCache, searchRegex),
    filterScanTemplateOption: getFilterTemplateOptionFunction(searchCache, searchRegex),
  };
};
export type ScanTemplateFilterFunctions = ReturnType<typeof getScanTemplateFilterFunctions>;
